/**
 * AuthService handles authentication state and persistence.
 * It uses localStorage for the token and IndexedDB for the full user object.
 * This service is optimized for production with robust error handling and efficient DB access.
 */
class AuthService {
  constructor() {
    this.dbName = "user";
    this.storeName = "asteria_user";
    // Incrementing version to 4 to trigger onupgradeneeded and fix store schema
    this.dbVersion = 4;
    this.userKey = "current_user"; // Constant key since we only store one user

    // Token remains in localStorage for immediate synchronous access
    this._token = window.localStorage.getItem("asteria_token");
    
    // User data is loaded asynchronously from IndexedDB
    this._user = null;

    /**
     * Promise that resolves when the service has finished initializing from the database.
     */
    this.initialized = this._initFromDB();
  }

  async _initFromDB() {
    try {
      const dbUser = await this.getUserFromWebDB();
      if (dbUser) {
        this._user = dbUser;
      }
      return this._user;
    } catch (error) {
      console.error("AuthService: Error initializing from IndexedDB", error);
      return null;
    }
  }

  get user() {
    return this._user;
  }

  /**
   * Setter for user. Updates memory and triggers async persistence.
   */
  set user(value) {
    this._user = value;
    if (value) {
      this.setUserOnWebDB(value).catch((err) =>
        console.error("AuthService: Failed to sync user to IndexedDB:", err)
      );
    } else {
      this.clearWebDB().catch((err) =>
        console.error("AuthService: Failed to clear user from IndexedDB:", err)
      );
    }
  }

  get token() {
    return this._token;
  }

  set token(value) {
    this._token = value;
    if (value) {
      window.localStorage.setItem("asteria_token", value);
    } else {
      window.localStorage.removeItem("asteria_token");
    }
  }

  get userTitle() {
    return this._user ? JSON.stringify(this._user) : "{}";
  }

  check() {
    return !!this.token;
  }

  /**
   * Updates the user data and reloads. Awaits DB write to ensure persistence survives reload.
   */
  async setUser(user, silent = false) {
    this._user = user;
    await this.setUserOnWebDB(user);
    if (!silent) location.reload();
  }

  /**
   * Logs in a user, sets token and user, then reloads. Awaits DB write.
   */
  async login(token, user) {
    this.token = token;
    this._user = user;
    await this.setUserOnWebDB(user);
    location.reload();
  }

  /**
   * Logs in an admin or switches context. Awaits DB cleanup and write.
   */
  async loginAdmin(token, user) {
    await this.clearWebDB();
    this.token = token;
    this._user = user;
    await this.setUserOnWebDB(user);
    window.location.href = "/";
  }

  /**
   * Impersonates a user by switching context. Awaits DB cleanup and write.
   */
  async loginAs(token, user) {
    await this.clearWebDB();
    this.token = token;
    this._user = user;
    await this.setUserOnWebDB(user);
    window.location.href = "/";
  }

  /**
   * Logs out the user by clearing all authentication data. Awaits DB cleanup.
   */
  async logout() {
    this.token = null;
    this._user = null;
    await this.clearWebDB();
    location.reload();
  }

  timerLogout(logoutMessage) {
    window.localStorage.setItem("sessionExpiry", logoutMessage);
  }

  _openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        // If the store already exists, we might need to recreate it if it has an incompatible keyPath
        if (db.objectStoreNames.contains(this.storeName)) {
          db.deleteObjectStore(this.storeName);
        }
        // Create store WITHOUT keyPath to allow external keys (userKey constant)
        db.createObjectStore(this.storeName);
      };
    });
  }

  async getUserFromWebDB() {
    try {
      const db = await this._openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(this.storeName, "readonly");
        const store = tx.objectStore(this.storeName);
        const request = store.get(this.userKey);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("AuthService: getUserFromWebDB failed", error);
      return null;
    }
  }

  async setUserOnWebDB(user) {
    if (!user) return;
    try {
      const db = await this._openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(this.storeName, "readwrite");
        const store = tx.objectStore(this.storeName);
        // Put user with the fixed key constant
        const request = store.put(user, this.userKey);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("AuthService: setUserOnWebDB failed", error);
    }
  }

  async clearWebDB() {
    try {
      const db = await this._openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(this.storeName, "readwrite");
        const store = tx.objectStore(this.storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("AuthService: clearWebDB failed", error);
    }
  }
}

export default new AuthService();
